import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { useIsRouteActive } from '@/components/layout/Navigation/hooks/useIsRouteActive';
import { useMobilePanel } from '@/components/layout/Navigation/mobile';
import { NavItem } from '@/components/layout/Navigation/sidebar/nav-item';
import {
  useIsChronicleDevDashboardEnabled,
  useIsRibbonSidebarEnabled,
} from '@/features/flags';
import { isTauri } from '@/tauri/utils';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { SidebarMenu } from '@heidi/design-system/page-shell';
import { useTranslations } from '@heidi/runtime/i18n';
import { Building, Computer, Radio, Settings } from 'lucide-react';

export interface SettingsGroupNavItemsProps {
  startTransition: (fn: () => void) => void;
  closeSidePanels: () => void;
  isGlobalAdmin: boolean;
  isMobile: boolean;
}

export function SettingsGroupNavItems({
  startTransition,
  closeSidePanels,
  isGlobalAdmin,
}: SettingsGroupNavItemsProps) {
  const { t } = useTranslations();
  const { dismissMobileSidebar } = useMobilePanel();
  const isRouteActive = useIsRouteActive();
  const isChronicleDevDashboardEnabled = useIsChronicleDevDashboardEnabled();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const { open: openSettingsModal } = useSettingsModal();

  const handleNavClick = (extra?: () => void) => {
    startTransition(() => {
      closeSidePanels();
      extra?.();
    });
    dismissMobileSidebar();
  };

  return (
    <SidebarMenu>
      {!isRibbonSidebarEnabled && (
        <>
          {isGlobalAdmin ? (
            <NavItem
              label={t('sidenav.organization.organization')}
              icon={<Building />}
              href="/organization"
              isActive={isRouteActive('/organization')}
              onClick={() => handleNavClick()}
              testId="sidebar-link-organization"
            />
          ) : (
            <NavItem
              label={t('sidenav.team')}
              icon={<Building />}
              href="/team"
              isActive={isRouteActive('/team')}
              onClick={() => handleNavClick()}
              testId="sidebar-link-team"
            />
          )}
        </>
      )}
      {!isRibbonSidebarEnabled && (
        <NavItem
          label={t('sidenav.settings.settings')}
          icon={<Settings />}
          isActive={false}
          onClick={() => handleNavClick(() => openSettingsModal(undefined, 'sidebar'))}
          testId="sidebar-link-settings"
        />
      )}
      {isTauri() && getEnvironment() !== ENV_NAME.PROD && (
        <NavItem
          label={t('sidenav.appInfo')}
          icon={<Computer />}
          href="/tauri-settings"
          isActive={isRouteActive('/tauri-settings')}
          onClick={() => handleNavClick()}
        />
      )}
      {isChronicleDevDashboardEnabled && (
        <NavItem
          label={t('sidenav.chronicleDev')}
          icon={<Radio />}
          href="/dev/chronicle"
          isActive={isRouteActive('/dev/chronicle')}
          onClick={() => handleNavClick()}
        />
      )}
    </SidebarMenu>
  );
}
